import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { subscribeNewsletter } from '../api';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { TRICOLOR } from './PageSection';

// Footer mirroring the original qasc.it: sedi, link utili (Privacy, Cookie,
// Whistleblowing), newsletter "Resta aggiornato" and the P.IVA strip.
const FooterSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#023144',
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    backgroundImage: TRICOLOR,
  },
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  marginBottom: '20px',
  fontFamily: '"Sora", sans-serif',
  fontSize: '15px',
  fontWeight: 700,
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
}));

const FooterLink = styled('a')(({ theme }) => ({
  display: 'block',
  color: 'rgba(255,255,255,0.72)',
  marginBottom: '11px',
  fontSize: '14px',
  cursor: 'pointer',
  textDecoration: 'none',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    color: '#8fc1d6',
    transform: 'translateX(4px)',
  }
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '14px',
  '& .MuiSvgIcon-root': {
    color: '#86a315',
    marginRight: '12px',
    marginTop: '2px',
    fontSize: '18px',
  }
}));

const ContactText = styled(Typography)(({ theme }) => ({
  color: 'rgba(255,255,255,0.72)',
  fontSize: '13.5px',
  lineHeight: 1.6,
}));

const EmailInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.07)',
    color: '#ffffff',
    borderRadius: '999px',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#8fc1d6',
    },
  },
  '& .MuiInputBase-input': {
    padding: '13px 20px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(255, 255, 255, 0.5)',
    opacity: 1,
  }
}));

const SubscribeButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#00425c',
  padding: '11px 28px',
  fontSize: '15px',
  '&:hover': {
    backgroundColor: '#e9f1f6',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 22px rgba(2, 49, 68, 0.45)',
  },
  '&.Mui-disabled': {
    backgroundColor: 'rgba(255,255,255,0.35)',
    color: 'rgba(0,66,92,0.6)'
  }
}));

function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  const handleSubscribe = async () => {
    if (!email) return;
    setIsSubmitting(true);
    try {
      await subscribeNewsletter(email);
      setEmail('');
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Grazie per esserti iscritto alla nostra newsletter!'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err.message || 'Qualcosa è andato storto. Scrivici a info@qasc.it.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((s) => ({ ...s, open: false }));
  };

  return (
    <>
      <FooterSection>
        <Box sx={{ py: { xs: '44px', md: '64px' } }}>
          <Container maxWidth="lg">
            <Grid container spacing={5}>
              {/* Company */}
              <Grid item xs={12} md={3}>
                <Box
                  component="img"
                  src="/assets/images/logo.png"
                  alt="QAS Consulting"
                  sx={{ height: '64px', mb: 2.5 }}
                />
                <ContactText sx={{ maxWidth: '280px' }}>
                  Servizi di formazione e consulenza aziendale continua: qualità, ambiente e
                  sicurezza al fianco della gestione d’impresa.
                </ContactText>
              </Grid>

              {/* Sedi */}
              <Grid item xs={12} sm={6} md={3}>
                <FooterTitle>QAS Consulting</FooterTitle>
                <ContactItem>
                  <LocationOnIcon />
                  <ContactText>
                    Sede operativa e aule formative<br />
                    Viale Sanremo, 36 — 30038 Spinea (VE)<br />
                    Telefono +39 041 995202
                  </ContactText>
                </ContactItem>
                <ContactItem>
                  <PhoneIcon />
                  <ContactText>
                    Sede operativa<br />
                    Via Monte Nero, 4 — 67043 Celano (AQ)<br />
                    Telefono / Fax +39 0863 792752
                  </ContactText>
                </ContactItem>
                <ContactItem>
                  <EmailIcon />
                  <ContactText>info@qasc.it · formazione@qasc.it</ContactText>
                </ContactItem>
                <ContactItem>
                  <AccessTimeIcon />
                  <ContactText>Lun – Ven / 08:00 – 18:00</ContactText>
                </ContactItem>
              </Grid>

              {/* Link utili */}
              <Grid item xs={12} sm={6} md={3}>
                <FooterTitle>Link utili</FooterTitle>
                <FooterLink href="/chi-siamo">Chi siamo</FooterLink>
                <FooterLink href="/formazione">Formazione</FooterLink>
                <FooterLink href="/consulenza">Consulenza</FooterLink>
                <FooterLink href="/corsi-in-evidenza">Corsi in evidenza</FooterLink>
                <FooterLink href="/news">News</FooterLink>
                <FooterLink href="https://qasc.it/privacy-policy/">Privacy Policy</FooterLink>
                <FooterLink href="https://qasc.it/cookie-policy/">Cookie Policy</FooterLink>
                <FooterLink
                  href="/docs/whistleblowing-qas-consulting.pdf"
                  target="_blank"
                  rel="noopener"
                >
                  Whistleblowing
                </FooterLink>
              </Grid>

              {/* Newsletter */}
              <Grid item xs={12} md={3}>
                <FooterTitle>Resta aggiornato</FooterTitle>
                <ContactText sx={{ mb: 2.5 }}>
                  Iscriviti alla newsletter per ricevere news e pillole di sicurezza.
                </ContactText>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <EmailInput
                    fullWidth
                    variant="outlined"
                    placeholder="Il tuo indirizzo e-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                  />
                  <SubscribeButton
                    variant="contained"
                    disableElevation
                    onClick={handleSubscribe}
                    disabled={isSubmitting || !email}
                  >
                    {isSubmitting ? 'Iscrizione…' : 'Invia'}
                  </SubscribeButton>
                  <ContactText sx={{ fontSize: '11px', lineHeight: 1.5 }}>
                    Acconsento al trattamento dei miei dati personali come dal Regolamento (UE)
                    2016/679 (GDPR).
                  </ContactText>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box sx={{ py: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Container maxWidth="lg">
            <Typography
              sx={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', fontSize: '13px' }}
            >
              © {new Date().getFullYear()} QAS Consulting | P.IVA 03999690278
            </Typography>
            <Typography sx={{ textAlign: 'center', fontSize: '12px', mt: '6px' }}>
              <Box
                component="a"
                href="https://hkmsp.com"
                target="_blank"
                rel="noopener"
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  textDecoration: 'none',
                  '&:hover': { color: '#8fc1d6' },
                }}
              >
                Built by HK Managed Services Provider Limited
              </Box>
            </Typography>
          </Container>
        </Box>
      </FooterSection>

      {/* Result message */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Footer;
